import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { secureGet, securePost } from "../../config/api";
import Input from "../../components/Input";
import { SaveCancel } from "../../components/PrimaryButton";


const RepoAdjustmentForm = ({
    title,
    baseFields = [],
    onSubmit,
    onSuccess,
    onCancel,
    apiUrl,
    showSnackbar,
}) => {
    const { t } = useTranslation();
    const [formValues, setFormValues] = useState({});
    const [systemQuantity, setSystemQuantity] = useState(null);
    const [loadingSystemQty, setLoadingSystemQty] = useState(false);
    const [saving, setSaving] = useState(false);
    const abortRef = useRef(null);
    const showSnackbarRef = useRef(showSnackbar);
    showSnackbarRef.current = showSnackbar;

    const gridFields = useMemo(
        () => baseFields.filter((f) => f.name !== "comment"),
        [baseFields]
    );
    const commentField = useMemo(
        () => baseFields.find((f) => f.name === "comment"),
        [baseFields]
    );

    const fieldsKey = useMemo(
        () => baseFields.map((f) => `${f.name}:${f.type || "text"}`).join("|"),
        [baseFields]
    );

    useEffect(() => {
        if (!fieldsKey) return;
        const initial = {};
        baseFields.forEach((f) => {
            initial[f.name] = "";
        });
        setFormValues(initial);
        setSystemQuantity(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when field set (names/types) changes
    }, [fieldsKey]);

    const productId = formValues.productId;
    const repoId = formValues.repoId;

    const fetchSystemQuantity = useCallback(async () => {
        if (!productId || !repoId) {
            setSystemQuantity(null);
            return;
        }
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        setLoadingSystemQty(true);
        setSystemQuantity(null);
        try {
            const response = await secureGet(
                `/products/${productId}/${repoId}/quantity`,
                showSnackbarRef.current,
                { signal: ac.signal }
            );
            setSystemQuantity(response.data);
        } catch (e) {
            if (e.name !== "AbortError" && e.code !== "ERR_CANCELED") {
                setSystemQuantity(null);
            }
        } finally {
            setLoadingSystemQty(false);
        }
    }, [productId, repoId]);

    useEffect(() => {
        if (productId && repoId) {
            fetchSystemQuantity();
        } else {
            setSystemQuantity(null);
        }
    }, [productId, repoId, fetchSystemQuantity]);

    const adjustedNum = useMemo(() => {
        const v = formValues.adjustedQuantity;
        if (v === "" || v == null) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }, [formValues.adjustedQuantity]);

    const calculatedEcart =
        systemQuantity != null && adjustedNum != null ? adjustedNum - systemQuantity : null;

    const ecartDisplay =
        calculatedEcart == null
            ? "—"
            : calculatedEcart > 0
                ? `+${calculatedEcart}`
                : String(calculatedEcart);

    const systemDisplay = loadingSystemQty ? "…" : systemQuantity != null ? String(systemQuantity) : "—";

    const handleChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        const missing = baseFields.filter(
            (f) => f.required && (formValues[f.name] === "" || formValues[f.name] == null)
        );
        if (missing.length) {
            showSnackbar?.(`Veuillez remplir les champs obligatoires: ${missing.map(f => f.label).join(', ')}`, "error", true);
            return;
        }
        if (systemQuantity == null && productId && repoId) {
            showSnackbar?.(t("fields.repoAdjustments.waitSystemQty"), "error", true);
            return;
        }
        if (adjustedNum == null) {
            showSnackbar?.("Quantité invalide.", "error", true);
            return;
        }

        const payload = {
            productId: Number(productId),
            repoId: Number(repoId),
            adjustedQuantity: adjustedNum,
            comment: String(formValues.comment || "").trim(),
            gap: calculatedEcart ?? adjustedNum - (systemQuantity ?? 0),
        };
        console.log(payload);

        try {
            setSaving(true);
            const response = await securePost(apiUrl, payload, showSnackbar);
            onSubmit?.(response?.data);
            onSuccess?.();
        } catch (e) {
            if (e.name !== "AbortError" && e.code !== "ERR_CANCELED") {
                console.error(e);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[12px] bg-[#F9FAFC] px-10 py-10">
                <h2 className="mb-8 text-[32px] font-semibold text-[#38485C]">{title}</h2>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {gridFields.map((field) => (
                        <Input
                            key={field.name}
                            type={field.type || "text"}
                            label={field.label}
                            name={field.name}
                            options={field.options || []}
                            value={formValues[field.name] ?? ""}
                            required={field.required}
                            onChange={(v) => handleChange(field.name, v)}
                        />
                    ))}
                </div>

                {commentField && (
                    <div className="mt-3 w-full">
                        <textarea
                            className="w-full h-48 rounded-lg border border-[#E3E3E3] bg-white py-3 px-[21px] text-[16px] font-medium text-[#000000] placeholder:text-[#AFB0B2] placeholder:text-[14px] placeholder:font-semibold focus:border-[#6A79F8] focus:outline-none hover:border-[#6A79F8]"
                            name="comment"
                            value={formValues.comment ?? ""}
                            onChange={(e) => handleChange("comment", e.target.value)}
                            placeholder="Commentaire"
                        />
                    </div>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex min-w-0 flex-wrap items-baseline gap-6 text-[16px] font-medium">
                        <p className="flex flex-wrap items-baseline gap-1">
                            <span className="text-[#231E29]">{t("fields.repoAdjustments.systemQuantity")}: </span>
                            <span className="text-[16px] font-bold text-[#043F88]">{systemDisplay}</span>
                        </p>
                        <p className="flex flex-wrap items-baseline gap-1">
                            <span className="text-[#231E29]">{t("fields.repoAdjustments.calculatedVariance")}: </span>
                            <span className="text-[16px] font-bold text-[#043F88]">{ecartDisplay}</span>
                        </p>
                    </div>
                    <SaveCancel onCancelClick={onCancel} onSaveClick={handleSave} saving={saving} />
                </div>
            </div>
        </div>
    );
};

export default RepoAdjustmentForm;
